import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import ReviewsList from "@/components/reviews/ReviewsList";
import ProfileHeader from "@/components/profile/ProfileHeader";
import STXProfileCard from "@/components/profile/STXProfileCard";
import { Heart, Sparkles, Flame } from "lucide-react";

/**
 * SellerProfilePage — bereichsspezifische Profil-Ansicht.
 *  - /user/:handle/marketplace  → area="MARKETPLACE" (unverändert, helle Optik)
 *  - /user/:handle/stx          → area="STILLTOUCHEDX" (neues STX-Theme, Light/Dark)
 */
export default function SellerProfilePage({ area = "MARKETPLACE" }) {
  const { t } = useTranslation();
  const { handle } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [listings, setListings] = useState([]);
  const [presence, setPresence] = useState({ SOCIAL: false, MARKETPLACE: false, STILLTOUCHEDX: false });
  const [followBusy, setFollowBusy] = useState(false);
  const [muted, setMuted] = useState(false);
  const [muteBusy, setMuteBusy] = useState(false);
  const [error, setError] = useState("");

  const isSTX = area === "STILLTOUCHEDX";
  const detailPath = isSTX ? "/stilltouchedx" : "/marketplace";

  useEffect(() => {
    if (!handle) return;
    (async () => {
      try {
        const { data: p } = await api.get(`/profiles/${handle}`);
        // P6-Merge: Im STX-Bereich immer auf den kanonischen stx_handle umleiten,
        // damit es nur EIN stilltouchedX-Profil gibt und kein Social-Handle leakt.
        if (isSTX && p.canonical_stx_handle && !p.accessed_via_stx && p.canonical_stx_handle !== handle) {
          navigate(`/user/${p.canonical_stx_handle}/stx`, { replace: true });
          return;
        }
        const [{ data: pres }, { data: l }] = await Promise.all([
          api.get(`/profiles/${handle}/area-presence`),
          api.get(`/profiles/${handle}/listings?area=${area}`),
        ]);
        setProfile(p);
        setPresence(pres.presence || {});
        setListings(l.listings || []);
        try {
          const { data: mutedList } = await api.get("/me/seller-mutes");
          setMuted((mutedList.muted || []).some((m) => m.seller_id === p.person_id));
        } catch { /* not logged in */ }
      } catch {
        setError(t("seller_profile_extra.profile_not_found"));
      }
    })();
  }, [handle, area, isSTX, navigate, t]);

  const toggleFollow = async () => {
    if (!user || user === false) { navigate("/login"); return; }
    if (!profile) return;
    // iter107c (Msg 806): follow-Endpoint erwartet person_id (nicht handle).
    // Vorher wurde handle gesendet → Follow wurde mit handle als following_id
    // gespeichert und tauchte nie in list_fans() auf.
    const targetId = profile.person_id;
    if (!targetId) return;
    const followArea = isSTX ? "STX" : "SOCIAL";
    setFollowBusy(true);
    try {
      if (profile.is_following) {
        await api.delete(`/profiles/${targetId}/follow?area=${followArea}`);
        setProfile({ ...profile, is_following: false, follow_request_pending: false, follower_count: Math.max(0, (profile.follower_count || 1) - 1) });
      } else if (profile.follow_request_pending) {
        await api.delete(`/profiles/${targetId}/follow?area=${followArea}`);
        setProfile({ ...profile, follow_request_pending: false });
      } else {
        const { data } = await api.post(`/profiles/${targetId}/follow?area=${followArea}`);
        if (data?.status === "requested") {
          setProfile({ ...profile, follow_request_pending: true });
        } else {
          setProfile({ ...profile, is_following: true, follower_count: (profile.follower_count || 0) + 1 });
        }
      }
    } catch { /* noop */ }
    finally { setFollowBusy(false); }
  };

  const toggleMute = async () => {
    if (!user || user === false) { navigate("/login"); return; }
    if (!profile) return;
    setMuteBusy(true);
    try {
      if (muted) {
        await api.delete(`/me/seller-mutes/${profile.person_id}`);
        setMuted(false);
      } else {
        await api.post("/me/seller-mutes", { seller_id: profile.person_id });
        setMuted(true);
      }
    } catch { /* noop */ }
    finally { setMuteBusy(false); }
  };

  const reportSeller = async () => {
    if (!user || user === false) { navigate("/login"); return; }
    const reason = window.prompt(t("seller_profile_extra.report_reason_prompt"), "OTHER");
    if (!reason) return;
    const description = window.prompt(t("seller_profile_extra.report_description_prompt"), "") || "";
    try {
      await api.post("/content-reports", { target_type: "USER", target_id: profile.person_id, reason: reason.toUpperCase(), description });
      alert(t("seller_profile_extra.report_thanks"));
    } catch (e) {
      alert(e.response?.data?.detail || t("seller_profile_extra.report_failed"));
    }
  };

  if (isSTX) {
    return (
      <STXSellerProfile
        profile={profile}
        error={error}
        listings={listings}
        presence={presence}
        followBusy={followBusy}
        muted={muted}
        muteBusy={muteBusy}
        toggleFollow={toggleFollow}
        toggleMute={toggleMute}
        reportSeller={reportSeller}
        navigate={navigate}
        detailPath={detailPath}
        area={area}
        isOwnProfile={!!(user && profile && profile.person_id && user._id === profile.person_id)}
      />
    );
  }

  const isOwnProfile = !!(user && profile && profile.person_id && user._id === profile.person_id);

  // MARKETPLACE — Legacy-Ansicht (unverändert)
  const themeBg = "bg-white text-[#0A0A0A]";
  const cardBg = "bg-white border-[#E5E5E5]";
  if (error) return <div className={`${themeBg} min-h-full flex items-center justify-center text-sm`}>{error}</div>;
  if (!profile) return <div className={`${themeBg} min-h-full flex items-center justify-center text-sm`}>{t("seller_profile_extra.loading")}</div>;

  return (
    <div data-testid={`seller-profile-${area.toLowerCase()}`} className={`${themeBg} min-h-full pb-16`}>
      <ProfileHeader
        profile={profile}
        area="MARKETPLACE"
        itemCount={listings.length}
        isOwn={isOwnProfile}
        isFollowing={!!profile.is_following}
        followRequested={!!profile.follow_request_pending}
        followBusy={followBusy}
        onFollow={toggleFollow}
        muted={muted}
        muteBusy={muteBusy}
        onMute={toggleMute}
        onReport={reportSeller}
        presence={presence}
        navigate={navigate}
        theme="light"
      />

      <h2 className="px-4 pt-3 pb-2 text-[10px] font-bold uppercase tracking-wider opacity-60">{t("seller_profile_extra.articles_mp")}</h2>
      {listings.length === 0 ? (
        <div className="px-4 py-12 text-center text-sm opacity-60">{t("seller_profile_extra.no_articles_mp")}</div>
      ) : (
        <div data-testid="seller-profile-grid" className="grid gap-3 p-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))" }}>
          {listings.map((l) => (
            <button key={l.listing_id} data-testid={`seller-listing-${l.listing_id}`} onClick={() => navigate(`${detailPath}/${l.listing_id}`)} className={`${cardBg} flex flex-col text-left rounded-2xl overflow-hidden border border-[#E5E5E5] hover:border-[#0A0A0A] transition-colors`}>
              <div className="relative aspect-[3/4] overflow-hidden bg-[#F5F5F5]">
                {l.media_urls?.[0] ? <img src={l.media_urls[0]} alt="" className="w-full h-full object-cover" loading="lazy" /> :
                  <div className="w-full h-full flex items-center justify-center opacity-30"><Heart size={18} /></div>}
              </div>
              <div className="p-2">
                <p className="text-xs font-medium truncate leading-tight">{l.title}</p>
                <p className="text-sm font-bold mt-0.5 text-[#0A0A0A]">{l.price?.toFixed(2)} {l.currency}</p>
              </div>
            </button>
          ))}
        </div>
      )}
      {/* iter88.21 · Sortenreine Marketplace-Bewertungen für diesen Seller */}
      <div className="px-4 pt-6 pb-2 border-t border-[#E5E5E5]" data-testid="seller-profile-reviews">
        <h2 className="text-[10px] font-bold uppercase tracking-wider opacity-60 mb-3">{t("seller_profile_extra.reviews_mp")}</h2>
        <ReviewsList scope="seller" scopeId={profile.person_id} area="MP" limit={10} emptyText={t("seller_profile_extra.no_reviews_mp")} />
      </div>
    </div>
  );
}

/* -------------------------------------------------------------
   STX-Variante — Valeszka-Style + STX-Theme (Light/Dark)
   ------------------------------------------------------------- */
function STXSellerProfile({
  profile, error, listings, presence, followBusy, muted, muteBusy,
  toggleFollow, toggleMute, reportSeller, navigate, detailPath, area, isOwnProfile = false,
}) {
  const { t } = useTranslation();
  const [fanStats, setFanStats] = useState(null);
  useEffect(() => {
    if (!profile?.handle) return;
    (async () => {
      try {
        const { data } = await api.get(`/profiles/${profile.handle}/fan-stats-public`);
        setFanStats(data);
      } catch { /* ignore */ }
    })();
  }, [profile?.handle]);
  if (error) {
    return <div className="bg-stx-bg text-stx-text min-h-full flex items-center justify-center text-sm">{error}</div>;
  }
  if (!profile) {
    return (
      <div className="bg-stx-bg text-stx-text min-h-full flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-stx-border border-t-stx-accent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid={`seller-profile-${area.toLowerCase()}`} className="bg-stx-bg text-stx-text min-h-full pb-20">
      <ProfileHeader
        profile={profile}
        area="STILLTOUCHEDX"
        itemCount={listings.length}
        isOwn={isOwnProfile}
        isFollowing={!!profile.is_following}
        followRequested={!!profile.follow_request_pending}
        followBusy={followBusy}
        onFollow={toggleFollow}
        muted={muted}
        muteBusy={muteBusy}
        onMute={toggleMute}
        onReport={reportSeller}
        presence={presence}
        navigate={navigate}
        theme="dark"
      />

      {/* iter212 · stilltouchedX Steckbrief NUR im STX-Profil sichtbar. */}
      {profile.seller_facets && Object.keys(profile.seller_facets).some((k) => profile.seller_facets[k]) && (
        <STXProfileCard facets={profile.seller_facets} />
      )}

      {/* iter88.16: Öffentlicher Trust-Chip — Top-Fans-Zahl (nur wenn > 0) */}
      {fanStats && fanStats.top_fans > 0 && (
        <div className="mt-3 flex justify-center">
          <span
            data-testid="seller-profile-top-fans"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-stx-accent-soft border border-stx-accent text-stx-accent text-[11px] font-semibold uppercase tracking-wider"
          >
            <Flame size={12} />
            {fanStats.top_fans} {t("seller_profile_extra.top_fans", { count: fanStats.top_fans })}
            {fanStats.active_fans > 0 && (
              <span className="text-stx-text-muted normal-case font-medium">
                &nbsp;· {fanStats.active_fans} {t("seller_profile_extra.active_suffix")}
              </span>
            )}
          </span>
        </div>
      )}

      {/* Section-Header */}
      <div className="px-5 pt-4 pb-3 flex items-center justify-between border-t border-stx-border">
        <h2 className="text-[10px] font-bold tracking-[0.2em] text-stx-text-dim inline-flex items-center gap-1.5">
          <Sparkles size={11} className="text-stx-accent" /> {t("seller_profile_extra.articles_stx_headline")}
        </h2>
        <span className="text-[10px] text-stx-text-dim tabular-nums">{listings.length}</span>
      </div>

      {/* Grid */}
      {listings.length === 0 ? (
        <div className="px-5 py-16 text-center">
          <div className="w-14 h-14 rounded-full bg-stx-accent-soft mx-auto flex items-center justify-center mb-3">
            <Heart size={20} className="text-stx-accent" />
          </div>
          <p className="text-sm text-stx-text-muted">{t("seller_profile_extra.no_articles_stx")}</p>
        </div>
      ) : (
        <div data-testid="seller-profile-grid" className="grid gap-3 px-4 pb-6" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))" }}>
          {listings.map((l) => (
            <button
              key={l.listing_id}
              data-testid={`seller-listing-${l.listing_id}`}
              onClick={() => navigate(`${detailPath}/${l.listing_id}`)}
              className="group rounded-xl overflow-hidden bg-stx-panel border border-stx-border hover:border-stx-accent transition-all text-left"
            >
              <div className="relative aspect-[3/4] overflow-hidden bg-stx-panel-2">
                {l.media_urls?.[0] ? (
                  <img src={l.media_urls[0]} alt="" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" loading="lazy" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-stx-text-dim">
                    <Heart size={20} />
                  </div>
                )}
                {l.rescue_phase === "RESCUE" && (
                  <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-stx-accent text-stx-accent-fg text-[9px] font-bold uppercase tracking-wider">
                    {t("seller_profile_extra.rescue_badge")}
                  </span>
                )}
              </div>
              <div className="p-2.5">
                <p className="text-xs text-stx-text truncate leading-tight">{l.title}</p>
                <p className="text-sm font-bold mt-1 text-stx-accent tabular-nums">
                  {l.price?.toFixed(2)} <span className="text-stx-text-muted text-[10px]">{l.currency}</span>
                </p>
              </div>
            </button>
          ))}
        </div>
      )}
      {/* iter88.21 · Sortenreine STX-Bewertungen (Reviewer anonymisiert als user{N}) */}
      <div className="px-4 pt-6 pb-6 border-t border-stx-border" data-testid="stx-seller-profile-reviews">
        <h2 className="text-[10px] font-bold uppercase tracking-[0.2em] text-stx-text-dim mb-3">{t("seller_profile_extra.reviews_stx")}</h2>
        <ReviewsList scope="seller" scopeId={profile.person_id} area="STX" limit={10} emptyText={t("seller_profile_extra.no_reviews_stx")} />
      </div>
    </div>
  );
}
